import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Fingerprint, KeyRound, LogOut, Smartphone, Shield, ArrowRight } from "lucide-react";
import { registerPasskey as browserRegisterPasskey } from "@/lib/passkey";
import { authenticateWithPasskey } from "@/lib/passkey";
import { registerPasskey, loginWithPasskey, listPasskeys, getWalletAddress } from "@/api/passkey";

interface SignSession {
  passkey: string;
  publicKeyX: string;
  publicKeyY: string;
  transaction: Record<string, any>;
  status: string;
}

export function PasskeyPage() {
  const [status, setStatus] = useState<string>("");
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [passkeys, setPasskeys] = useState<Array<{
    id: string;
    credentialId: string;
    name: string | null;
    createdAt: string;
  }>>([]);
  const [signSession, setSignSession] = useState<SignSession | null>(null);
  const [txResult, setTxResult] = useState<any>(null);
  const [waitingForTap, setWaitingForTap] = useState(false);
  const tapHandled = useRef(false);

  useEffect(() => {
    // Check if index.html already auto-signed via first-tick bypass
    if ((window as any).__passkeyAutoSigned && (window as any).__passkeyCredential) {
      const cred = (window as any).__passkeyCredential;
      const sid = (window as any).__passkeySessionId;
      handleAutoSignCredential(cred, sid);
      return;
    }

    // Also listen for the event from index.html script
    const onAutoSign = (e: any) => {
      if (e.detail && e.detail.credential) {
        handleAutoSignCredential(e.detail.credential, e.detail.sessionId);
      }
    };
    window.addEventListener("passkey:auto-sign", onAutoSign);

    const url = new URL(window.location.href);
    const action = url.searchParams.get("action");
    const sessionId = url.searchParams.get("session");

    if (action === "sign" && sessionId) {
      loadSignSession(sessionId);
      return;
    }

    // Check auth via secure HttpOnly cookie (not localStorage token)
    (async () => {
      try {
        const sessRes = await fetch("/api/auth/get-session", {
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        if (sessRes.ok) {
          const sessData = await sessRes.json().catch(() => null);
          if (sessData?.user?.id) {
            setHasSession(true);
            setIsLoading(false);
            loadPasskeys();
            return;
          }
        }
      } catch {
        // ignore — fall through to manual flow
      }
    })();

    // On mobile, show tap-to-continue overlay
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile && window.PublicKeyCredential) {
      setWaitingForTap(true);
      setStatusMsg("Tap to authenticate", false);
    } else {
      setShowManual(true);
    }
    setIsLoading(false);
    return () => {
      window.removeEventListener("passkey:auto-sign", onAutoSign);
    };
  }, []);

  const setStatusMsg = (msg: string, error = false) => {
    setStatus(msg);
    setIsError(error);
  };

  const loadSignSession = async (sessionId: string) => {
    setIsLoading(true);
    setStatusMsg("Loading transaction...");
    try {
      const res = await fetch(`/api/auth/passkey/sign-session?sessionId=${sessionId}`);
      const data = await res.json();
      if (data.success) {
        setSignSession(data);
        setWaitingForTap(true);
        setStatusMsg("Tap to sign with Face ID", false);
      } else {
        setStatusMsg(data.error?.message || "Session not found", true);
      }
    } catch (e) {
      setStatusMsg("Failed to load signing session", true);
    } finally {
      setIsLoading(false);
    }
  };

  const loadPasskeys = async () => {
    try {
      const data = await listPasskeys();
      setPasskeys(data.passkeys ?? []);

      // Recover wallet address from API if localStorage was cleared
      if (!localStorage.getItem("otc_passkey_address") && data.passkeys && data.passkeys.length > 0) {
        for (const pk of data.passkeys) {
          if (pk.credentialId) {
            try {
              const wa = await getWalletAddress(pk.credentialId);
              if (wa.walletAddress) {
                localStorage.setItem("otc_passkey_address", wa.walletAddress);
                break;
              }
            } catch (e) {
              // ignore per-credential errors
            }
          }
        }
      }
    } catch (e) {
      localStorage.removeItem("otc_passkey_session");
      setHasSession(false);
      setShowManual(true);
    }
  };

  // CRITICAL: Direct call in event handler — Safari requires this for user gesture
  const onTapAnywhere = () => {
    if (tapHandled.current) return;
    tapHandled.current = true;
    setWaitingForTap(false);

    if (signSession) {
      doSignTransaction();
    } else {
      doCreatePasskey();
    }
  };

  // Direct (non-async wrapper) — navigator.credentials called synchronously in click handler
  const doSignTransaction = () => {
    if (!signSession) return;
    setIsLoading(true);
    setStatusMsg("Authenticating...");

    const rpId = window.location.hostname === "localhost" ? undefined : window.location.hostname;
    const challenge = crypto.getRandomValues(new Uint8Array(32));

    navigator.credentials.get({
      publicKey: {
        challenge: challenge as any,
        rpId,
        userVerification: "required" as const,
        timeout: 120000,
      },
    }).then((credential) => {
      if (!credential) throw new Error("No credential");
      const pkCred = credential as PublicKeyCredential;
      const response = pkCred.response as AuthenticatorAssertionResponse;

      // Build base64url strings
      const sig = btoa(String.fromCharCode(...new Uint8Array(response.signature)))
        .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
      const authData = btoa(String.fromCharCode(...new Uint8Array(response.authenticatorData)))
        .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
      const clientData = btoa(String.fromCharCode(...new Uint8Array(response.clientDataJSON)))
        .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

      setStatusMsg("Verifying...");

      return fetch("/api/auth/passkey/sign-transfer", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          credentialId: pkCred.id,
          passkey: signSession.passkey,
          signature: sig,
          authenticatorData: authData,
          clientDataJSON: clientData,
          publicKey: {
            x: signSession.publicKeyX,
            y: signSession.publicKeyY,
          },
          transaction: signSession.transaction,
        }),
      });
    }).then((res) => res.json()).then((data) => {
      if (data.success) {
        setTxResult(data);
        setStatusMsg("Transaction signed!", false);
      } else {
        setStatusMsg(data.message || "Signing failed", true);
      }
    }).catch((e: any) => {
      if (e.name === "NotAllowedError") {
        setStatusMsg("Authentication cancelled", true);
        tapHandled.current = false;
        setWaitingForTap(true);
      } else {
        setStatusMsg(e.message || "Signing failed", true);
      }
    }).finally(() => {
      setIsLoading(false);
    });
  };

  const doCreatePasskey = () => {
    setIsLoading(true);
    setStatusMsg("Creating passkey...");

    const rpId = window.location.hostname === "localhost" ? undefined : window.location.hostname;
    const challenge = crypto.getRandomValues(new Uint8Array(32));
    const userId = crypto.getRandomValues(new Uint8Array(32));

    navigator.credentials.create({
      publicKey: {
        challenge: challenge as any,
        rp: { name: "OTC", ...(rpId && { id: rpId }) },
        user: { id: userId as any, name: "θ", displayName: "θ" },
        pubKeyCredParams: [{ type: "public-key" as const, alg: -7 }],
        authenticatorSelection: {
          authenticatorAttachment: "platform" as const,
          userVerification: "required" as const,
          residentKey: "required" as const,
        },
        timeout: 120000,
        attestation: "direct" as const,
      },
    }).then((credential) => {
      if (!credential) throw new Error("Cancelled");
      const pkCred = credential as PublicKeyCredential;
      const response = pkCred.response as AuthenticatorAttestationResponse;

      const pkBuffer = (response as any).getPublicKey();
      if (!pkBuffer) throw new Error("No public key");

      const pkArray = new Uint8Array(pkBuffer);
      let publicKeyX = "";
      let publicKeyY = "";

      if (pkArray.length > 64) {
        const xBytes = pkArray.slice(-64, -32);
        const yBytes = pkArray.slice(-32);
        publicKeyX = "0x" + Array.from(xBytes).map((b: number) => b.toString(16).padStart(2, "0")).join("");
        publicKeyY = "0x" + Array.from(yBytes).map((b: number) => b.toString(16).padStart(2, "0")).join("");
      } else {
        const xBytes = pkArray.slice(0, 32);
        const yBytes = pkArray.slice(32, 64);
        publicKeyX = "0x" + Array.from(xBytes).map((b: number) => b.toString(16).padStart(2, "0")).join("");
        publicKeyY = "0x" + Array.from(yBytes).map((b: number) => b.toString(16).padStart(2, "0")).join("");
      }

      return registerPasskey({
        credentialId: pkCred.id,
        publicKeyX,
        publicKeyY,
        name: "OTC Passkey",
      });
    }).then((result) => {
      if (result.success) {
        setStatusMsg("Passkey created!", false);
        setHasSession(true);
        if (result.walletAddress) {
          localStorage.setItem("otc_passkey_address", result.walletAddress);
        }
        return loadPasskeys();
      } else {
        setShowManual(true);
        setStatusMsg(result.message || "Failed", true);
      }
    }).catch((e: any) => {
      if (e.name === "NotAllowedError") {
        setStatusMsg("Cancelled — tap to try again", true);
        tapHandled.current = false;
        setWaitingForTap(true);
      } else {
        setShowManual(true);
        setStatusMsg(e.message || "Failed", true);
      }
    }).finally(() => {
      setIsLoading(false);
    });
  };

  // Called when index.html first-tick bypass succeeds
  const handleAutoSignCredential = (credential: any, sessionId: string) => {
    setIsLoading(true);
    setStatusMsg("Auto-authenticating...");

    const pkCred = credential as PublicKeyCredential;
    const response = pkCred.response as AuthenticatorAssertionResponse;

    const sig = btoa(String.fromCharCode(...new Uint8Array(response.signature)))
      .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
    const authData = btoa(String.fromCharCode(...new Uint8Array(response.authenticatorData)))
      .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
    const clientData = btoa(String.fromCharCode(...new Uint8Array(response.clientDataJSON)))
      .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

    fetch("/api/auth/passkey/sign-transfer", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        credentialId: pkCred.id,
        passkey: signSession?.passkey,
        signature: sig,
        authenticatorData: authData,
        clientDataJSON: clientData,
        publicKey: {
          x: signSession?.publicKeyX,
          y: signSession?.publicKeyY,
        },
        transaction: signSession?.transaction,
      }),
    }).then((res) => res.json()).then((data) => {
      if (data.success) {
        setTxResult(data);
        setStatusMsg("Transaction signed!", false);
      } else {
        setStatusMsg(data.message || "Signing failed", true);
      }
    }).catch((e: any) => {
      setStatusMsg(e.message || "Signing failed", true);
    }).finally(() => {
      setIsLoading(false);
    });
  };

  const handleLogin = async () => {
    setIsLoading(true);
    setStatusMsg("Authenticating...");

    try {
      const assertion = await authenticateWithPasskey();
      const result = await loginWithPasskey(assertion);

      if (result.success) {
        // Session cookie is set HttpOnly by the backend — no localStorage token
        setStatusMsg("Logged in!", false);
        setHasSession(true);
        await loadPasskeys();
      } else {
        setStatusMsg("Login failed", true);
      }
    } catch (e: any) {
      setStatusMsg(e.message || "Login failed", true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("otc_passkey_session");
    setHasSession(false);
    setPasskeys([]);
    setShowManual(false);
    setWaitingForTap(false);
    setStatusMsg("Session cleared", false);
  };

  // ─── Signing Mode ───────────────────────────────────────────────────────
  if (signSession) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center p-4">
        <div className="bg-[#13131f] rounded-[20px] p-6 w-full max-w-[420px] text-center shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
          <div className="w-12 h-12 bg-[#d4f034]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-6 h-6 text-[#d4f034]" />
          </div>
          <h1 className="text-xl font-semibold mb-2">Sign Transaction</h1>
          <p className="text-[#8890a3] text-sm mb-4">Authorize with your passkey</p>

          {txResult ? (
            <div className="bg-[#1a1a2e] rounded-xl p-4 mb-4 text-left">
              <div className="text-[#4ade80] text-sm font-medium mb-2">Transaction Sent!</div>
              <div className="font-mono text-[11px] text-[#8890a3] break-all">
                {txResult.transactionHash}
              </div>
            </div>
          ) : (
            <>
              <div className="bg-[#1a1a2e] rounded-xl p-4 mb-4 text-left">
                <div className="text-xs text-[#8890a3] mb-1">Transaction</div>
                {Object.entries(signSession.transaction).map(([key, val]) => (
                  <div key={key} className="flex justify-between text-sm mb-1">
                    <span className="text-[#8890a3]">{key}:</span>
                    <span className="text-white font-mono text-xs break-all max-w-[200px]">
                      {String(val)}
                    </span>
                  </div>
                ))}
              </div>

              {waitingForTap ? (
                <button
                  onClick={onTapAnywhere}
                  className="w-full py-4 bg-[#d4f034] text-black rounded-xl font-semibold animate-pulse flex items-center justify-center"
                >
                  <Fingerprint className="w-5 h-5 mr-2" />
                  Tap to sign with Face ID
                </button>
              ) : (
                <Button
                  onClick={doSignTransaction}
                  disabled={isLoading}
                  className="w-full bg-[#d4f034] text-black hover:opacity-90 rounded-xl py-4 text-base font-semibold h-auto min-h-[56px]"
                >
                  {isLoading ? "Signing..." : (
                    <>
                      <Fingerprint className="w-5 h-5 mr-2" />
                      Sign Transaction
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </>
          )}

          {status && !txResult && (
            <div className={"mt-4 text-[13px] " + (isError ? "text-[#f87171]" : "text-[#4ade80]")}>
              {status}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── Normal Passkey Mode ────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center p-4">
      <div className="bg-[#13131f] rounded-[20px] p-6 sm:p-10 w-full max-w-[420px] text-center shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
        <div className="w-12 h-12 bg-[#d4f034]/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Smartphone className="w-6 h-6 text-[#d4f034]" />
        </div>
        <h1 className="text-xl sm:text-[22px] font-semibold mb-2">
          {hasSession ? "Your Passkeys" : "OTC Passkey"}
        </h1>
        <p className="text-[#8890a3] text-sm mb-6">
          {hasSession ? `${passkeys.length} passkey(s) registered` : "Secure your OTC wallet with biometrics"}
        </p>

        {!hasSession ? (
          <>
            {waitingForTap ? (
              <button
                onClick={onTapAnywhere}
                className="w-full py-4 bg-[#d4f034] text-black rounded-xl font-semibold animate-pulse flex items-center justify-center mb-3"
              >
                <Fingerprint className="w-5 h-5 mr-2" />
                Tap to create passkey
              </button>
            ) : showManual ? (
              <>
                <Button
                  onClick={doCreatePasskey}
                  disabled={isLoading}
                  className="w-full mb-3 bg-[#d4f034] text-black hover:opacity-90 rounded-xl py-4 text-base font-semibold h-auto min-h-[56px]"
                >
                  <Fingerprint className="w-5 h-5 mr-2" />
                  Create Passkey
                </Button>
                <Button
                  onClick={handleLogin}
                  disabled={isLoading}
                  variant="outline"
                  className="w-full bg-transparent text-[#8890a3] border-[#2a2a3a] hover:bg-transparent hover:text-[#8890a3] rounded-xl py-4 text-base font-semibold h-auto min-h-[56px]"
                >
                  <KeyRound className="w-5 h-5 mr-2" />
                  Login with Passkey
                </Button>
              </>
            ) : null}
          </>
        ) : (
          <>
            <div className="text-left mt-4 pt-4 border-t border-[#2a2a3a]">
              {passkeys.map((pk: any) => (
                <div key={pk.id} className="bg-[#1a1a2e] rounded-xl p-4 mb-3">
                  <div className="font-mono text-[13px] text-[#d4f034] break-all">
                    {pk.credentialId}
                  </div>
                  <div className="text-xs text-[#8890a3] mt-1">
                    Created: {new Date(pk.createdAt).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={handleLogout}
              className="mt-4 text-[13px] text-[#f87171] bg-transparent border-none cursor-pointer underline flex items-center justify-center mx-auto"
            >
              <LogOut className="w-4 h-4 mr-1" />
              Clear session
            </button>
          </>
        )}

        {status && (
          <div className={"mt-4 text-[13px] min-h-[20px] " + (isError ? "text-[#f87171]" : "text-[#4ade80]")}>
            {status}
          </div>
        )}
      </div>
    </div>
  );
}
